import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Modal, notification, Select, Table, Tag } from "antd";

import { PAGE, PAGE_NAME } from "@utils/constants";
import StickyBreadcrumbPageComponent from "@layouts/stickyBreadcrumbPage/stickyBreadcrumbPage";
import {
  createCustomFieldDef,
  deleteCustomFieldDef,
  listCustomFieldDefs,
  updateCustomFieldDef,
  validateFieldDef,
} from "@apis/erent2Application";

const BREADCRUMB = [{ name: PAGE_NAME.ERENT2_CUSTOM_FIELDS, path: PAGE.ERENT2_CUSTOM_FIELDS }];
const TYPES = { text: "文字", select: "單選", multiselect: "多選" };
const EMPTY_FIELD = { key: "", label: "", type: "text", options: [] };
const errorMessage = (error) =>
  [error.response?.data?.error, ...(error.response?.data?.details || [])].filter(Boolean).join(" ") ||
  "操作失敗，請重試";

function Erent2CustomFieldsPage() {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [field, setField] = useState(EMPTY_FIELD);
  const [keyEdited, setKeyEdited] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listCustomFieldDefs();
      setDefinitions(response.data || response || []);
    } catch (loadError) {
      notification.error({ message: errorMessage(loadError) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (definition) => {
    setEditing(definition || null);
    setField(definition ? { ...definition, options: definition.options || [] } : EMPTY_FIELD);
    setKeyEdited(Boolean(definition));
    setError("");
    setVisible(true);
  };

  const changeLabel = (event) => {
    const label = event.target.value;
    setField((current) => ({
      ...current,
      label,
      key: keyEdited ? current.key : label.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40),
    }));
  };

  const save = async () => {
    const validationError = validateFieldDef(field);
    if (validationError) return setError(validationError);
    setSaving(true);
    const body = {
      key: field.key,
      label: field.label.trim(),
      type: field.type,
      options: field.type === "text" ? undefined : field.options.map((o) => o.trim()),
    };
    try {
      if (editing) await updateCustomFieldDef(editing._id, body);
      else await createCustomFieldDef(body);
      setVisible(false);
      await load();
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const remove = (definition) => Modal.confirm({
    title: "確認刪除自訂欄位",
    content: `刪除「${definition.label}」會同時移除所有申請記錄內的此欄位值，此操作不可復原。`,
    okType: "danger",
    okText: "刪除",
    cancelText: "取消",
    onOk: async () => {
      try { await deleteCustomFieldDef(definition._id); await load(); }
      catch (deleteError) { notification.error({ message: errorMessage(deleteError) }); }
    },
  });

  const columns = [
    { title: "名稱", dataIndex: "label", key: "label" },
    { title: "鍵值", dataIndex: "key", key: "key" },
    { title: "類型", dataIndex: "type", key: "type", render: (type) => TYPES[type] || type },
    { title: "選項", dataIndex: "options", key: "options", render: (options = []) => options.length ? options.map((option) => <Tag key={option}>{option}</Tag>) : "—" },
    { title: "操作", key: "actions", render: (_, definition) => <><Button type="link" onClick={() => open(definition)}>編輯</Button><Button type="link" style={{ color: "#f5222d" }} onClick={() => remove(definition)}>刪除</Button></> },
  ];

  return <StickyBreadcrumbPageComponent breadcrumb={BREADCRUMB}>
    <div />
    <div style={{ padding: "16px 24px" }}>
      <div style={{ marginBottom: 16, textAlign: "right" }}><Button type="primary" onClick={() => open()}>新增欄位</Button></div>
      <Table rowKey="_id" loading={loading} dataSource={definitions} columns={columns} pagination={false} />
      <Modal title={editing ? "編輯欄位" : "新增欄位"} visible={visible} confirmLoading={saving} okText="儲存" cancelText="取消" onOk={save} onCancel={() => setVisible(false)}>
        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        <div style={{ marginBottom: 12 }}>名稱<Input maxLength={100} value={field.label} onChange={changeLabel} /></div>
        <div style={{ marginBottom: 12 }}>鍵值<Input maxLength={40} value={field.key} onChange={(e) => { setKeyEdited(true); setField({ ...field, key: e.target.value }); }} /></div>
        <div style={{ marginBottom: 12 }}>類型<Select style={{ width: "100%" }} value={field.type} onChange={(type) => setField({ ...field, type })}>{Object.keys(TYPES).map((type) => <Select.Option key={type} value={type}>{TYPES[type]}</Select.Option>)}</Select></div>
        {field.type !== "text" && <div>選項
          {field.options.map((option, index) => <div key={index} style={{ display: "flex", marginBottom: 8 }}><Input maxLength={100} value={option} onChange={(e) => setField({ ...field, options: field.options.map((item, i) => i === index ? e.target.value : item) })} /><Button type="link" style={{ color: "#f5222d" }} onClick={() => setField({ ...field, options: field.options.filter((_, i) => i !== index) })}>移除</Button></div>)}
          <Button disabled={field.options.length >= 50} onClick={() => setField({ ...field, options: [...field.options, ""] })}>新增選項</Button>
        </div>}
      </Modal>
    </div>
  </StickyBreadcrumbPageComponent>;
}

export default Erent2CustomFieldsPage;
